/**
 * Shared logging utilities for interceptors
 */

import type { InterceptorConfig } from "../types";

export function shouldLog(config: InterceptorConfig): boolean {
  const { enableLogging, enableLoggingInProduction = false } = config;

  if (!enableLogging) {
    return false;
  }

  if (enableLoggingInProduction) {
    return true;
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nodeProcess = (globalThis as any).process;
    if (nodeProcess && nodeProcess.env?.NODE_ENV === "production") {
      return false;
    }
  } catch {
    // process not available, continue
  }

  return true;
}

/**
 * Sensitive headers that should be redacted in logs
 */
const SENSITIVE_HEADERS = [
  "authorization",
  "cookie",
  "x-api-key",
  "x-auth-token",
  "x-access-token",
  "x-csrf-token",
] as const;

/**
 * Sanitizes headers by redacting sensitive information
 * @param headers - Headers object to sanitize
 * @returns Sanitized headers object
 */
export function sanitizeHeaders(
  headers: Record<string, unknown>,
): Record<string, unknown> {
  const sanitized = { ...headers };

  Object.keys(sanitized).forEach((key) => {
    const lowerKey = key.toLowerCase();
    if (SENSITIVE_HEADERS.some((header) => lowerKey === header)) {
      sanitized[key] = "[REDACTED]";
    }
  });

  return sanitized;
}
